/**
 * Admin Marketing — Crew API. `/api/admin/v2/marketing/crew/*`
 *
 * Collaborators (the internal people and external contacts), the sources each
 * one speaks for, and the review threads on work in progress.
 *
 * Two directions on the same join, because both are real questions and neither
 * should be answered by loading the other side whole:
 *   - `useCollaboratorSourcesQuery(mcid)` — "what does this person cover?"
 *   - `useSourceContactsQuery(kind, id)`  — "who do I email about this account?"
 * The second is what the Sources list pages need for their contact chips; doing
 * it client-side would mean fetching the entire directory to render four names.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  ApiCollaborator,
  ApiCollaboratorSourceLink,
  ApiSourceContact,
  ApiWorkFeedback,
  MarketingList,
  MarketingListParams,
} from '@/client/types/marketing/api';
import type { SourceKind } from '@/client/types/marketing/sources';
import { MARKETING_BASE, marketingKeys } from './keys';

/* --------------------------------------------------------- collaborators */

/**
 * The contacts directory.
 *
 * Pass `with_sources: 1` when the caller needs each contact's source links —
 * the Contacts list filters and searches on them. Leave it off for pickers and
 * dropdowns, which should not pay for the join.
 */
export const useCollaboratorsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiCollaborator>>>({
    defaultErrorMessage: 'Could not load contacts',
    endpoint: `${MARKETING_BASE}/crew/collaborators`,
  });
  return useQuery({
    queryKey: marketingKeys.crew.collaborators(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useCreateCollaboratorMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiCollaborator>, AdminEnvelope<ApiCollaborator>>({
    defaultErrorMessage: 'Could not create the contact',
    endpoint: `${MARKETING_BASE}/crew/collaborators`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.crew.all }),
  });
};

export const useUpdateCollaboratorMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiCollaborator> & { mcid: number },
    AdminEnvelope<ApiCollaborator>
  >({
    defaultErrorMessage: 'Could not update the contact',
    endpoint: (payload) => `${MARKETING_BASE}/crew/collaborators/${payload.mcid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.crew.all }),
  });
};

export const useDeleteCollaboratorMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ mcid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the contact',
    endpoint: (payload) => `${MARKETING_BASE}/crew/collaborators/${payload.mcid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    // Sources pages render contact chips off the reverse lookup, so a removed
    // contact has to disappear from those too — not just from the directory.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.crew.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
    },
  });
};

/* ------------------------------------------------- collaborator ↔ source */

export const useCollaboratorSourcesQuery = (mcid: number, enabled = true) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{ mcid: number; count: number; links: ApiCollaboratorSourceLink[] }>
  >({
    defaultErrorMessage: 'Could not load the sources for this contact',
    endpoint: `${MARKETING_BASE}/crew/collaborators/${mcid}/sources`,
  });
  return useQuery({
    queryKey: marketingKeys.crew.collaboratorSources(mcid),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(mcid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * Attach a contact to an account or outlet.
 *
 * Idempotent on the (contact, source) pair: re-linking an existing pair updates
 * `is_account_holder` instead of creating a second row, so promoting someone to
 * account holder does not leave them listed twice on the card.
 */
export const useLinkCollaboratorSourceMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    {
      mcid: number;
      source_kind: SourceKind;
      source_id: number;
      is_account_holder?: boolean;
    },
    AdminEnvelope<ApiCollaboratorSourceLink>
  >({
    defaultErrorMessage: 'Could not link the source',
    endpoint: (payload) => `${MARKETING_BASE}/crew/collaborators/${payload.mcid}/sources`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: marketingKeys.crew.collaboratorSources(variables.mcid),
      });
      queryClient.invalidateQueries({
        queryKey: marketingKeys.sources.contacts(variables.source_kind, variables.source_id),
      });
    },
  });
};

/**
 * Detach a contact from a source.
 *
 * `mcid` is not part of the URL — the link id identifies it — but it is required
 * in the payload so the right caches can be invalidated without re-reading the
 * row that was just deleted.
 */
export const useUnlinkCollaboratorSourceMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { mcid: number; mcsid: number; source_kind?: SourceKind; source_id?: number },
    AdminEnvelope<{ deleted: boolean; mcsid: number }>
  >({
    defaultErrorMessage: 'Could not unlink the source',
    endpoint: (payload) =>
      `${MARKETING_BASE}/crew/collaborators/${payload.mcid}/sources/${payload.mcsid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: marketingKeys.crew.collaboratorSources(variables.mcid),
      });
      if (variables.source_kind && variables.source_id != null) {
        queryClient.invalidateQueries({
          queryKey: marketingKeys.sources.contacts(variables.source_kind, variables.source_id),
        });
      } else {
        queryClient.invalidateQueries({ queryKey: marketingKeys.sources.all });
      }
    },
  });
};

/**
 * Who to contact about one account or outlet. Account holders sort first.
 *
 * Pass `enabled: false` while the source id is still unknown — the hook already
 * refuses a non-finite id, but a list page mapping over rows should not fire a
 * request per row before the list itself has loaded.
 */
export const useSourceContactsQuery = (
  sourceKind: SourceKind,
  sourceId: number,
  enabled = true,
) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{
      source_kind: SourceKind;
      source_id: number;
      count: number;
      contacts: ApiSourceContact[];
    }>
  >({
    defaultErrorMessage: 'Could not load contacts for this source',
    endpoint: `${MARKETING_BASE}/sources/${sourceKind}/${sourceId}/contacts`,
  });
  return useQuery({
    queryKey: marketingKeys.sources.contacts(sourceKind, sourceId),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(sourceId),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * Reverse index of contacts by source for list pages.
 *
 * One `with_sources=1` collaborators fetch replaces an N+1 of
 * `useSourceContactsQuery` per table row. React Query dedupes this across every
 * `SourceContactsCell` on the page.
 */
export const useSourceContactsIndex = () => {
  const query = useCollaboratorsQuery({ limit: 500, with_sources: 1 });

  const bySource = useMemo(() => {
    const map = new Map<string, ApiSourceContact[]>();
    for (const c of query.data?.items ?? []) {
      for (const link of c.source_links ?? []) {
        const sourceId = link.source_kind === 'account' ? link.said : link.moid;
        if (sourceId == null) continue;
        const key = `${link.source_kind}:${sourceId}`;
        const entry: ApiSourceContact = {
          ...c,
          mcsid: link.mcsid,
          is_account_holder: link.is_account_holder,
        };
        const list = map.get(key) ?? [];
        list.push(entry);
        map.set(key, list);
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) => Number(b.is_account_holder) - Number(a.is_account_holder));
    }
    return map;
  }, [query.data]);

  return {
    bySource,
    isLoading: query.isLoading,
    isError: query.isError,
    contactsFor: (sourceKind: SourceKind, sourceId: number) =>
      bySource.get(`${sourceKind}:${sourceId}`) ?? [],
  };
};

/* -------------------------------------------------------------- feedback */

export const useWorkFeedbackQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiWorkFeedback>>>({
    defaultErrorMessage: 'Could not load feedback',
    endpoint: `${MARKETING_BASE}/crew/feedback`,
  });
  return useQuery({
    queryKey: marketingKeys.crew.feedback(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useCreateWorkFeedbackMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiWorkFeedback>, AdminEnvelope<ApiWorkFeedback>>({
    defaultErrorMessage: 'Could not post the note',
    endpoint: `${MARKETING_BASE}/crew/feedback`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.crew.all }),
  });
};

/**
 * Close or reopen a review thread.
 *
 * Not a PATCH: `resolved_at` is server-set and deliberately absent from the
 * writable fields, so a caller cannot backdate when a review point was closed.
 * Reopening clears both the timestamp and the resolver, so a thread closed twice
 * does not keep the first resolution's attribution.
 */
export const useResolveWorkFeedbackMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { mwfid: number; resolved: boolean; resolved_by_mcid?: number },
    AdminEnvelope<ApiWorkFeedback>
  >({
    defaultErrorMessage: 'Could not update the thread',
    endpoint: (payload) =>
      `${MARKETING_BASE}/crew/feedback/${payload.mwfid}/${payload.resolved ? 'resolve' : 'reopen'}`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.crew.all }),
  });
};
